import { ButtonHTMLAttributes, forwardRef } from 'react'
import { cn } from '@/utils/cn'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'success' | 'glow' | 'gradient' | 'gradient-blend'
  size?: 'sm' | 'md' | 'lg'
  isLoading?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', isLoading, children, disabled, ...props }, ref) => {
    const baseStyles = 'inline-flex items-center justify-center rounded-xl font-semibold transition-all duration-200 focus:outline-none disabled:opacity-50 disabled:pointer-events-none active:scale-95'

    const variants = {
      primary: 'bg-primary-500 text-white hover:bg-primary-600 shadow-lg shadow-primary-500/30 hover:shadow-primary-500/50 hover:-translate-y-0.5',
      secondary: 'bg-accent-500 text-white hover:bg-accent-600 shadow-lg shadow-accent-500/30 hover:shadow-accent-500/50 hover:-translate-y-0.5',
      outline: 'border-2 border-primary-500 text-primary-500 hover:bg-primary-50 hover:border-primary-400 hover:shadow-md',
      ghost: 'text-primary-500 hover:bg-primary-50',
      danger: 'bg-error-500 text-white hover:bg-error-600 shadow-lg shadow-error-500/30 hover:shadow-error-500/50 hover:-translate-y-0.5',
      success: 'bg-success-500 text-white hover:bg-success-600 shadow-lg shadow-success-500/30 hover:shadow-success-500/50 hover:-translate-y-0.5',
      glow: 'bg-gradient-primary text-white shadow-glow-primary hover:shadow-glow-primary-lg hover:-translate-y-0.5 hover:scale-105',
      gradient: 'bg-gradient-accent text-white shadow-glow-accent hover:shadow-glow-accent-lg hover:-translate-y-0.5 hover:scale-105',
      'gradient-blend': 'bg-gradient-blend text-white hover:bg-gradient-blend-reverse shadow-glow-primary hover:shadow-glow-primary-lg hover:-translate-y-0.5 hover:scale-105',
    }

    const sizes = {
      sm: 'px-4 py-2 text-sm',
      md: 'px-6 py-2.5 text-base',
      lg: 'px-8 py-3 text-lg',
    }

    return (
      <button
        ref={ref}
        className={cn(baseStyles, variants[variant], sizes[size], className)}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading ? (
          <>
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            Yükleniyor...
          </>
        ) : children}
      </button>
    )
  }
)
